/**
 * ObjectTracker - weak reference tracking for engine objects.
 */


import { ClassRegistry } from './ClassRegistry.js';

export interface TrackedObjectInfo {
  ref: WeakRef<object>;
  className: string;
  createdAt: number;
}

export interface AliveObjectStats {
  className: string;
  count: number;
}

class ObjectTrackerImpl {
  private trackedObjects: Map<string, TrackedObjectInfo> = new Map();
  private objectsByClass: Map<string, Set<string>> = new Map();
  private idCounter: number = 0;

  /**
   * Hardcoded flag to enable/disable object tracking.
   * @default false
   */
  public enabled: boolean = true;

  /** @returns true if tracking is enabled, false otherwise */
  public isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Tracks an object using a weak reference.
   * @returns A unique tracking ID for the object, or empty string if tracking is disabled
   */
  public track(obj: object): string {
    if (!this.enabled) {
      return '';
    }
    const resolvedClassName = ClassRegistry.getClassOrConstructorName(obj);

    // console.debug('track object:', resolvedClassName);

    const id = `${resolvedClassName}_${++this.idCounter}_${Date.now()}`;
    const ref = new WeakRef(obj);

    this.trackedObjects.set(id, {
      ref,
      className: resolvedClassName,
      createdAt: Date.now(),
    });

    // Add to class-based index
    if (!this.objectsByClass.has(resolvedClassName)) {
      this.objectsByClass.set(resolvedClassName, new Set());
    }
    this.objectsByClass.get(resolvedClassName)!.add(id);

    return id;
  }

  /**
   * Gets statistics of alive objects per class. Automatically cleans up dead references.
   * @returns Array of AliveObjectStats sorted by class name
   */
  public getAliveObjectStats(): AliveObjectStats[] {
    const aliveCounts = new Map<string, number>();
    const deadIds: string[] = [];

    // Check all tracked objects
    for (const [id, info] of this.trackedObjects) {
      const obj = info.ref.deref();
      if (obj === undefined) {
        // Object has been garbage collected
        deadIds.push(id);
      } else {
        // Object is still alive
        aliveCounts.set(info.className, (aliveCounts.get(info.className) ?? 0) + 1);
      }
    }

    // Clean up dead references
    for (const id of deadIds) {
      this.removeTrackedObject(id);
    }

    // Convert to sorted array
    const stats: AliveObjectStats[] = [];
    for (const [className, count] of aliveCounts) {
      stats.push({ className, count });
    }

    // Sort by count descending, then by class name ascending
    stats.sort((a, b) => {
      if (b.count !== a.count) {
        return b.count - a.count;
      }
      return a.className.localeCompare(b.className);
    });

    return stats;
  }

  /** @returns Total number of tracked object entries */
  public getTotalTrackedCount(): number {
    return this.trackedObjects.size;
  }

  /**
   * Cleans up a tracked object by its ID.
   * @param id The tracking ID to remove
   */
  private removeTrackedObject(id: string): void {
    const info = this.trackedObjects.get(id);
    if (info) {
      // Remove from class index
      const classSet = this.objectsByClass.get(info.className);
      if (classSet) {
        classSet.delete(id);
        if (classSet.size === 0) {
          this.objectsByClass.delete(info.className);
        }
      }
      this.trackedObjects.delete(id);
    }
  }

  /** Clears all tracked objects and resets the tracker. Useful for testing or when resetting engine state. */
  public clear(): void {
    this.trackedObjects.clear();
    this.objectsByClass.clear();
    this.idCounter = 0;
  }

  /**
   * Gets detailed information about alive objects of a specific class.
   * @returns Array of objects that are still alive
   */
  public getAliveObjectsByClass(className: string): object[] {
    const alive: object[] = [];
    const classSet = this.objectsByClass.get(className);

    if (!classSet) {
      return alive;
    }

    const deadIds: string[] = [];

    for (const id of classSet) {
      const info = this.trackedObjects.get(id);
      if (info) {
        const obj = info.ref.deref();
        if (obj !== undefined) {
          alive.push(obj);
        } else {
          deadIds.push(id);
        }
      }
    }

    // Clean up dead references
    for (const id of deadIds) {
      this.removeTrackedObject(id);
    }

    return alive;
  }
}

// Singleton instance
export const ObjectTracker = new ObjectTrackerImpl();
